import { AlertCircle, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

import { useOrganizationClients } from "../hooks/useOrganizationClients";
import { canEditSubclient, canReadClientAccounts, canReadClients } from "../utils/clientsPermissions";
import { ClientAccountsModal } from "./ClientAccountsModal";
import { ClientsFiltersBar } from "./ClientsFiltersBar";
import { ClientsKpiStrip } from "./ClientsKpiStrip";
import { EditClientModal } from "./EditClientModal";
import { OrganizationClientsTable } from "./OrganizationClientsTable";
import { ViewClientModal } from "./ViewClientModal";

export interface OrganizationClientsViewProps {
  /** Scope the list to one organization (internal drill-down). Omit for the org user's own list. */
  organizationId?: string;
  /** Show the KPI strip above the filters (used on the org user's root view). */
  showKpis?: boolean;
  /** Increment to force a refetch after an external mutation (e.g. the create-client modal). */
  refreshToken?: number;
  /** Opens the create-client flow from the empty state CTA. */
  onCreateClient?: () => void;
  /** Notifies the host after a mutation inside this view (edit/delete) so it can refresh counters. */
  onDataChanged?: () => void;
}

/**
 * Server-paginated clients list with filters, KPI strip, table states, and the client-level modals (detail
 * drawer, contact edit, accounts). Reused as the organization user's root view and as level 2 of the internal
 * master-detail.
 */
export function OrganizationClientsView({
  organizationId,
  showKpis = false,
  refreshToken = 0,
  onCreateClient,
  onDataChanged,
}: OrganizationClientsViewProps) {
  const { t } = useTranslations("clients");
  const { user } = useAuth();
  const query = useOrganizationClients(organizationId);

  const [viewClientId, setViewClientId] = useState<string | null>(null);
  const [editClientId, setEditClientId] = useState<string | null>(null);
  const [accountsModal, setAccountsModal] = useState<{ clientId: string | null; displayName: string }>({
    clientId: null,
    displayName: "",
  });

  const refetch = query.refetch;
  const lastRefreshToken = useRef(refreshToken);
  useEffect(() => {
    if (refreshToken !== lastRefreshToken.current) {
      lastRefreshToken.current = refreshToken;
      refetch();
    }
  }, [refreshToken, refetch]);

  const handleMutationSuccess = () => {
    refetch();
    onDataChanged?.();
  };

  /**
   * Detail drawer → accounts drawer. Both are `ColocaDrawer`s and cannot stack, so the handoff is a swap and
   * it lives here, where both are mounted. Re-opening the client afterwards is deliberately not automatic:
   * its detail is stale once accounts change, so the operator reopens it and gets a fresh read.
   */
  const handleManageAccounts = useCallback((clientId: string, displayName: string) => {
    setViewClientId(null);
    setAccountsModal({ clientId, displayName });
  }, []);

  return (
    <div className="space-y-4">
      {showKpis && <ClientsKpiStrip scope="clients" total={query.total} isLoading={query.isLoading} />}

      <ClientsFiltersBar
        mode="clients"
        filters={query.filters}
        setFilters={query.setFilters}
        appliedFilters={query.appliedFilters}
        onApply={query.applyFilters}
        onReset={query.resetFilters}
        onRemoveFilter={query.removeFilter}
        isLoading={query.isLoading}
      />

      {query.error && (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {query.error}
              <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={refetch}>
                {t("error_retry")}
              </ColocaButton>
            </span>
          }
        />
      )}

      <OrganizationClientsTable
        rows={query.rows}
        isLoading={query.isLoading}
        hasActiveFilters={query.hasActiveFilters}
        page={query.page}
        pageSize={query.pageSize}
        total={query.total}
        onPageChange={query.setPage}
        onPageSizeChange={query.setPageSize}
        onViewClient={canReadClients(user) ? setViewClientId : undefined}
        onEditClient={canEditSubclient(user) ? setEditClientId : undefined}
        onViewAccounts={
          canReadClientAccounts(user)
            ? (clientId, displayName) => setAccountsModal({ clientId, displayName })
            : undefined
        }
        onClearFilters={query.resetFilters}
        onCreateClient={onCreateClient}
      />

      <ViewClientModal
        isOpen={viewClientId !== null}
        onClose={() => setViewClientId(null)}
        clientId={viewClientId}
        onClientDeleted={handleMutationSuccess}
        onManageAccounts={canReadClientAccounts(user) ? handleManageAccounts : undefined}
      />

      <EditClientModal
        isOpen={editClientId !== null}
        onClose={() => setEditClientId(null)}
        clientId={editClientId}
        onSuccess={handleMutationSuccess}
      />

      {/* Rows are clients, so their accounts always live under the client endpoints — including for
          INTERNAL staff drilling into an organization. */}
      <ClientAccountsModal
        isOpen={accountsModal.clientId !== null}
        onClose={() => setAccountsModal({ clientId: null, displayName: "" })}
        entityId={accountsModal.clientId}
        entityDisplayName={accountsModal.displayName}
        scope="client"
      />
    </div>
  );
}
